import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAccountConsumption,
  getAccountBilling,
  getTariffs,
  assignAccountTariff,
  getMyConsumption,
  getMyBilling
} from '../services/consumption';
import { getAccounts } from '../services/accounts';
import { isSystemAdmin } from '../services/auth';
import DateRangeSelector from '../components/Consumption/DateRangeSelector';
import ProgressBar from '../components/Consumption/ProgressBar';
import LineChartWidget from '../components/Charts/LineChartWidget';
import './AccountConsumptionPage.css';

const AccountConsumptionPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const isSysAdmin = isSystemAdmin();

  // Date range defaults to current month
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };

  const [selectedAccountId, setSelectedAccountId] = useState(accountId || '');
  const [accounts, setAccounts] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [billing, setBilling] = useState(null);
  const [records, setRecords] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [measureType, setMeasureType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const itemsPerPage = 20;

  useEffect(() => {
    if (isSysAdmin) {
      loadAccounts();
      loadTariffs();
    }
  }, [isSysAdmin]);

  useEffect(() => {
    if (accountId) {
      setSelectedAccountId(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (isSysAdmin && selectedAccountId) {
      loadAccountData();
    } else if (!isSysAdmin) {
      loadMyAccountData();
    }
  }, [selectedAccountId, dateRange, measureType, currentPage, isSysAdmin]);

  const loadAccounts = async () => {
    try {
      const data = await getAccounts(0, 100);
      setAccounts(data.accounts || data);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadTariffs = async () => {
    try {
      const data = await getTariffs(true);
      setTariffs(data);
    } catch (err) {
      console.error('Error loading tariffs:', err);
    }
  };

  const loadAccountData = async () => {
    if (!selectedAccountId) return;

    try {
      setLoading(true);
      setError('');

      const skip = (currentPage - 1) * itemsPerPage;

      const [billingData, consumptionData] = await Promise.all([
        getAccountBilling(selectedAccountId, dateRange.startDate, dateRange.endDate),
        getAccountConsumption(
          selectedAccountId,
          dateRange.startDate,
          dateRange.endDate,
          measureType || null,
          skip,
          itemsPerPage
        )
      ]);

      setBilling(billingData);
      setRecords(consumptionData.records || consumptionData);
      setTotalRecords(consumptionData.total || consumptionData.length);

      // Generate daily data for chart
      generateDailyData(consumptionData.records || consumptionData);
    } catch (err) {
      setError('Error al cargar datos de la cuenta');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyAccountData = async () => {
    try {
      setLoading(true);
      setError('');

      const skip = (currentPage - 1) * itemsPerPage;

      const [billingData, consumptionData] = await Promise.all([
        getMyBilling(dateRange.startDate, dateRange.endDate),
        getMyConsumption(
          dateRange.startDate,
          dateRange.endDate,
          measureType || null,
          skip,
          itemsPerPage
        )
      ]);

      setBilling(billingData);
      setRecords(consumptionData.records || consumptionData);
      setTotalRecords(consumptionData.total || consumptionData.length);

      generateDailyData(consumptionData.records || consumptionData);
    } catch (err) {
      setError('Error al cargar datos de consumo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateDailyData = (records) => {
    const dailyMap = {};

    records.forEach(record => {
      if (record.measure_type === 'ANALYSIS') {
        const date = record.date_time?.split('T')[0];
        if (date) {
          dailyMap[date] = (dailyMap[date] || 0) + 1;
        }
      }
    });

    const data = Object.entries(dailyMap)
      .map(([date, count]) => ({ date, value: count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    setDailyData(data);
  };

  const handleAccountChange = (e) => {
    const newAccountId = e.target.value;
    setSelectedAccountId(newAccountId);
    setCurrentPage(1);
    if (newAccountId) {
      navigate(`/consumption/${newAccountId}`, { replace: true });
    }
  };

  const handleTariffChange = async (e) => {
    const tariffId = e.target.value ? parseInt(e.target.value) : null;
    try {
      await assignAccountTariff(selectedAccountId, tariffId);
      loadAccountData();
    } catch (err) {
      setError('Error al asignar tarifa');
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '-';
    const date = new Date(dateTime);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatResponseTime = (ms) => {
    if (!ms) return '-';
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)} s`;
    return `${ms} ms`;
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  return (
    <div className="account-consumption-page">
      <div className="page-header">
        <div className="header-left">
          {isSysAdmin && (
            <button onClick={() => navigate('/consumption')} className="btn-back">
              &larr; Volver
            </button>
          )}
          <h1>{isSysAdmin ? 'Consumo de Cuenta' : 'Mi Consumo'}</h1>
        </div>
        <DateRangeSelector
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(range) => {
            setDateRange(range);
            setCurrentPage(1);
          }}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Account Selector - solo superadmin */}
      {isSysAdmin && (
        <div className="account-selector-section">
          <div className="form-group">
            <label>Cuenta</label>
            <select value={selectedAccountId} onChange={handleAccountChange}>
              <option value="">Selecciona una cuenta</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {((isSysAdmin && selectedAccountId) || !isSysAdmin) && billing && (
        <>
          {/* Billing Section */}
          <div className="billing-section">
            <h2>Facturacion</h2>
            <div className="billing-grid">
              <div className="billing-card">
                <div className="billing-row">
                  <span className="billing-label">Tarifa:</span>
                  {isSysAdmin ? (
                    <select
                      value={billing.tariff_id || ''}
                      onChange={handleTariffChange}
                      className="tariff-select"
                    >
                      <option value="">Sin tarifa</option>
                      {tariffs.map(t => (
                        <option key={t.hash} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="billing-value">{billing.tariff_name || 'Sin tarifa'}</span>
                  )}
                </div>

                <ProgressBar
                  current={billing.queries_used || 0}
                  max={billing.queries_included || 0}
                  label="Analisis realizados"
                />

                <div className="billing-stats">
                  <div className="billing-stat">
                    <span className="stat-value">{billing.queries_extra || 0}</span>
                    <span className="stat-label">Analisis extra</span>
                  </div>
                  <div className="billing-stat">
                    <span className="stat-value">
                      {billing.total_extra_cost?.toFixed(2) || '0.00'} EUR
                    </span>
                    <span className="stat-label">Coste extra</span>
                  </div>
                  <div className="billing-stat">
                    <span className="stat-value">
                      {billing.avg_tokens_per_query?.toLocaleString() || '-'}
                    </span>
                    <span className="stat-label">Tokens/analisis (media)</span>
                  </div>
                </div>
              </div>

              <div className="billing-card tokens-card">
                <h3>Tokens consumidos</h3>
                <div className="tokens-grid">
                  <div className="token-stat">
                    <span className="token-value">
                      {billing.total_tokens_used?.toLocaleString() || 0}
                    </span>
                    <span className="token-label">Total</span>
                  </div>
                  <div className="token-stat">
                    <span className="token-value">
                      {billing.total_input_tokens?.toLocaleString() || 0}
                    </span>
                    <span className="token-label">Entrada</span>
                  </div>
                  <div className="token-stat">
                    <span className="token-value">
                      {billing.total_output_tokens?.toLocaleString() || 0}
                    </span>
                    <span className="token-label">Salida</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Chart */}
          {dailyData.length > 0 && (
            <div className="chart-section">
              <LineChartWidget
                data={dailyData}
                title="Analisis por dia"
                dataKey="value"
                xKey="date"
              />
            </div>
          )}

          {/* Records Table */}
          <div className="records-section">
            <div className="records-header">
              <h2>Registros de consumo</h2>
              <div className="filter-group">
                <label>Tipo:</label>
                <select
                  value={measureType}
                  onChange={(e) => {
                    setMeasureType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="ANALYSIS">Analisis</option>
                  <option value="OPERATION">Operacion</option>
                </select>
              </div>
            </div>

            {loading && !records.length && <p>Cargando...</p>}

            <div className="records-table-container">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Fecha/hora</th>
                    <th>Analisis</th>
                    <th>Tipo</th>
                    <th>Tokens entrada</th>
                    <th>Tokens salida</th>
                    <th>Total tokens</th>
                    <th>Tiempo</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr key={record.hash || idx}>
                      <td>{formatDateTime(record.date_time)}</td>
                      <td className="hash-cell" title={record.analysis_hash}>
                        {record.filename || record.analysis_hash || '-'}
                      </td>
                      <td>
                        <span className={`type-badge ${record.measure_type?.toLowerCase()}`}>
                          {record.measure_type === 'ANALYSIS'
                            ? 'Analisis'
                            : (record.operation_name || 'Operacion')}
                        </span>
                      </td>
                      <td>{record.input_tokens?.toLocaleString() || 0}</td>
                      <td>{record.output_tokens?.toLocaleString() || 0}</td>
                      <td>{record.total_tokens?.toLocaleString() || 0}</td>
                      <td>{formatResponseTime(record.response_time_ms)}</td>
                    </tr>
                  ))}
                  {!loading && records.length === 0 && (
                    <tr>
                      <td colSpan={7} className="empty-message">
                        No hay registros de consumo
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                <span>
                  Pagina {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountConsumptionPage;
